import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Stack,
  Divider,
  Box,
  CircularProgress,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Avatar,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Add, CloudDownload, Save } from "@mui/icons-material";
import { getPolicyById } from "services/api";
import PolicyNode from "../policyNode";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import { getImageSrc } from "services/utils";
import { addPolicy } from "services/api";
import { exportPolicy } from "services/api";
import { useTranslation } from "react-i18next";
import { hasEditAccess } from "services/utils";

const staticContent = [
  {
    number: "1",
    title: "Purpose",
    children: [],
  },
  {
    number: "2",
    title: "Applicable To",
    children: [],
  },
  {
    number: "3",
    title: "Definition",
    children: [],
  },
  {
    number: "4",
    title: "Policy Statement",
    children: [],
  },
  {
    number: "5",
    title: "Responsibility",
    children: [],
  },
  {
    number: "6",
    title: "Procedures",
    children: [],
  },
  {
    number: "7",
    title: "Attachments",
    children: [],
  },
  {
    number: "8",
    title: "References",
    children: [],
  },
];

const staticContentAR = [
  {
    number: "1",
    title: "الهدف من السياسة",
    children: [],
  },
  {
    number: "2",
    title: "يسرى على",
    children: [],
  },
  {
    number: "3",
    title: "التعريفات",
    children: [],
  },
  {
    number: "4",
    title: "بيان السياسة",
    children: [],
  },
  {
    number: "5",
    title: "المسئوليات",
    children: [],
  },
  {
    number: "6",
    title: "الاجراءات الخاصة بالهيكل التنظيمى",
    children: [],
  },
  {
    number: "7",
    title: "المرفقات",
    children: [],
  },
  {
    number: "8",
    title: "المراجع",
    children: [],
  },
];

const PolicyEditor = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const id = location.state?.id;
  const { companies, employee, modules } = useAuth();
  const [policyData, setPolicyData] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (id) {
      getPolicyById(id).then((data) => {
        if (data !== "error") {
          setPolicyData(data);
        }
      });
    } else {
      setPolicyData({
        title: "",
        documentIdentifier: "",
        documentOriginator: "",
        effectiveDate: null,
        expiryDate: null,
        replaces: "",
        issueNo: "",
        revisionNo: "",
        needForReview: "",
        content: employee.language === "en" ? staticContent : staticContentAR,
      });
    }
    setCanEdit(hasEditAccess(modules, "Policies", employee));
  }, [id, employee.language]);

  const updateNode = (list, number, callback) =>
    list.map((item) => {
      if (item.number === number) return callback(item);
      if (item.children) {
        item.children = updateNode(item.children, number, callback);
      }
      return item;
    });

  const handleEdit = (number, newTitle) => {
    setPolicyData((prev) => ({
      ...prev,
      content: updateNode(prev.content, number, (node) => ({ ...node, title: newTitle })),
    }));
  };

  const handleAdd = (parentNumber) => {
    setPolicyData((prev) => ({
      ...prev,
      content: updateNode(prev.content, parentNumber, (node) => {
        const newNumber = `${parentNumber}.${(node.children?.length || 0) + 1}`;
        const newChild = { number: newNumber, title: "", children: [] };
        return { ...node, children: [...(node.children || []), newChild] };
      }),
    }));
  };

  const handleDelete = (numberToDelete) => {
    // Prevent deletion of top-level nodes (1-8)
    if (/^\d+$/.test(numberToDelete)) {
      return;
    }

    setPolicyData((prev) => {
      const updateNumbers = (nodes, parentPath = []) => {
        return nodes
          .filter((node) => node.number !== numberToDelete)
          .map((node, index) => {
            const parts = [...parentPath, index + 1];
            const newNumber = parts.join(".");

            return {
              ...node,
              number: newNumber,
              children: node.children ? updateNumbers(node.children, parts) : [],
            };
          });
      };

      return {
        ...prev,
        content: updateNumbers(prev.content),
      };
    });
  };

  const handleSave = async () => {
    setSaveLoading(true);
    // await updatePolicy(id, policyData?);
    if (!policyData.title.trim()) {
      setSnackbarMessage(t("admin.policies.snackbar.titleEmpty"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setSaveLoading(false);
      return;
    }
    const pol = await addPolicy(policyData);
    if (pol != "error") {
      setPolicyData(pol);
      setSnackbarMessage(t("admin.policies.snackbar.save"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage(t("admin.policies.snackbar.duplicateTitle"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setSaveLoading(false);
  };

  const handleFieldChange = (field) => (e) => {
    setPolicyData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleDownload = async () => {
    if (!id && !policyData?.id) return;
    setDownloadLoading(true);
    await exportPolicy(policyData.id, policyData.title, employee.language === "en" ? 1 : 2);
    setDownloadLoading(false);
  };

  if (!policyData)
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress size={20} />
      </Box>
    );

  const tableCellStyles = {
    padding: isMobile ? "8px" : "12px 16px",
    borderRight: "1px solid #e0e0e0",
    "&:last-child": { borderRight: "none" },
  };

  // Responsive avatar sizing
  const avatarStyles = {
    width: isMobile ? 80 : 120,
    height: isMobile ? 40 : 60,
  };

  // Responsive save button
  const saveButtonStyles = {
    width: isMobile ? "100%" : "auto",
    mt: isMobile ? 2 : 0,
    mb: isMobile ? 2 : 0,
  };

  const renderMobileTable = () => (
    <Box sx={{ mb: 4 }}>
      {/* Logo and Document ID */}
      <Box sx={{ display: "flex", mb: 2, alignItems: "center" }}>
        <Avatar src={getImageSrc(companies[0]?.companyLogo)} sx={avatarStyles} variant="square" />
        <TextField
          fullWidth
          sx={{ ml: 2 }}
          value={policyData?.documentIdentifier}
          disabled={!canEdit}
          onChange={handleFieldChange("documentIdentifier")}
          variant="outlined"
          size="small"
          label={t("admin.policies.editor.documentId")}
        />
      </Box>

      {/* Title */}
      <TextField
        fullWidth
        value={policyData?.title}
        onChange={handleFieldChange("title")}
        variant="outlined"
        disabled={!canEdit}
        size="small"
        label={t("admin.policies.editor.title")}
        sx={{ mb: 2 }}
      />

      {/* Dates */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          type="date"
          value={policyData?.effectiveDate || ""}
          onChange={handleFieldChange("effectiveDate")}
          variant="outlined"
          disabled={!canEdit}
          size="small"
          label={t("admin.policies.editor.effectiveDate")}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          type="date"
          value={policyData?.expiryDate || ""}
          onChange={handleFieldChange("expiryDate")}
          variant="outlined"
          disabled={!canEdit}
          size="small"
          label={t("admin.policies.editor.expiryDate")}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Version Info */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          value={policyData?.issueNo}
          onChange={handleFieldChange("issueNo")}
          variant="outlined"
          disabled={!canEdit}
          size="small"
          label={t("admin.policies.editor.issueNo")}
        />
        <TextField
          fullWidth
          value={policyData?.revisionNo}
          onChange={handleFieldChange("revisionNo")}
          variant="outlined"
          disabled={!canEdit}
          size="small"
          label={t("admin.policies.editor.revisionNo")}
        />
      </Box>

      {/* Other Fields */}
      <TextField
        fullWidth
        value={policyData?.documentOriginator}
        onChange={handleFieldChange("documentOriginator")}
        variant="outlined"
        disabled={!canEdit}
        size="small"
        label={t("admin.policies.editor.documentOriginator")}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        value={policyData?.needForReview}
        onChange={handleFieldChange("needForReview")}
        variant="outlined"
        disabled={!canEdit}
        size="small"
        label={t("admin.policies.editor.needForReview")}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        value={policyData?.replaces}
        onChange={handleFieldChange("replaces")}
        variant="outlined"
        disabled={!canEdit}
        size="small"
        label={t("admin.policies.editor.replaces")}
      />
    </Box>
  );

  const renderDesktopTable = () => (
    <Table
      sx={{
        mb: 4,
        border: "1px solid #e0e0e0",
        "& .MuiTableCell-root": {
          padding: "12px 16px",
          borderRight: "1px solid #e0e0e0",
          "&:last-child": {
            borderRight: "none",
          },
        },
      }}
    >
      <TableBody>
        {/* First Row */}
        <TableRow>
          <TableCell sx={{ width: "30%", verticalAlign: "middle" }}>
            <Avatar
              src={getImageSrc(companies[0]?.companyLogo)}
              variant="square"
              sx={{ width: 120, height: 60 }}
            />
          </TableCell>
          <TableCell>
            <TextField
              fullWidth
              value={policyData?.documentIdentifier}
              disabled={!canEdit}
              onChange={handleFieldChange("documentIdentifier")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.documentId")}
            />
          </TableCell>
        </TableRow>

        {/* Second Row */}
        <TableRow>
          <TableCell sx={{ verticalAlign: "middle" }}>
            <TextField
              fullWidth
              value={policyData?.title}
              disabled={!canEdit}
              onChange={handleFieldChange("title")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.title")}
            />
          </TableCell>
          <TableCell>
            <TextField
              type="date"
              fullWidth
              value={policyData?.effectiveDate || ""}
              disabled={!canEdit}
              onChange={handleFieldChange("effectiveDate")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.effectiveDate")}
              InputLabelProps={{ shrink: true }}
            />
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                value={policyData?.issueNo}
                disabled={!canEdit}
                onChange={handleFieldChange("issueNo")}
                variant="outlined"
                size="small"
                label={t("admin.policies.editor.issueNo")}
              />
              <TextField
                fullWidth
                value={policyData?.revisionNo}
                disabled={!canEdit}
                onChange={handleFieldChange("revisionNo")}
                variant="outlined"
                size="small"
                label={t("admin.policies.editor.revisionNo")}
              />
            </Stack>
          </TableCell>
        </TableRow>

        {/* Third Row */}
        <TableRow>
          <TableCell sx={{ verticalAlign: "middle" }}>
            <TextField
              fullWidth
              value={policyData?.documentOriginator}
              disabled={!canEdit}
              onChange={handleFieldChange("documentOriginator")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.documentOriginator")}
            />
          </TableCell>
          <TableCell>
            <TextField
              type="date"
              fullWidth
              value={policyData?.expiryDate || ""}
              disabled={!canEdit}
              onChange={handleFieldChange("expiryDate")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.expiryDate")}
              InputLabelProps={{ shrink: true }}
            />
          </TableCell>
          <TableCell>
            <TextField
              fullWidth
              value={policyData?.needForReview}
              disabled={!canEdit}
              onChange={handleFieldChange("needForReview")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.needForReview")}
            />
          </TableCell>
        </TableRow>

        {/* Last Row */}
        <TableRow>
          <TableCell
            sx={{
              verticalAlign: "middle",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "1rem",
            }}
          >
            {policyData?.title}
          </TableCell>
          <TableCell>
            <TextField
              fullWidth
              value={policyData?.replaces}
              disabled={!canEdit}
              onChange={handleFieldChange("replaces")}
              variant="outlined"
              size="small"
              label={t("admin.policies.editor.replaces")}
            />
          </TableCell>
          <TableCell>{/* Empty cell as requested */}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, p: isMobile ? 1 : 3 }}>
      <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" mb={2}>
        <Typography variant="h5">{t("admin.policies.editor.editorTitle")}</Typography>
        {saveLoading ? (
          <CircularProgress size={24} />
        ) : (
          <MDButton
            variant="contained"
            color="success"
            disabled={!canEdit}
            onClick={handleSave}
            startIcon={<Save />}
            sx={saveButtonStyles}
          >
            {t("admin.policies.editor.save")}
          </MDButton>
        )}
        {downloadLoading ? (
          <CircularProgress size={24} />
        ) : (
          <MDButton color="primary" onClick={handleDownload} startIcon={<CloudDownload />}>
            {t("export")}
          </MDButton>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Responsive table rendering */}
      {isMobile ? renderMobileTable() : renderDesktopTable()}

      <Divider sx={{ mb: 2 }} />

      {/* Content Sections */}
      <Typography variant="h6" gutterBottom>
        {t("admin.policies.editor.contentTitle")}
      </Typography>
      {policyData?.content?.map((section) => (
        <PolicyNode
          key={section.number}
          node={section}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isMobile={isMobile}
          canEdit={canEdit}
        />
      ))}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: isMobile ? "top" : "bottom",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PolicyEditor;
